<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core';
import { ArrowRight } from 'lucide-vue-next';

// The row this route shares with the other criteria: the route may take the room its siblings
// leave in it, which is what gets measured.
const properties = defineProps<{ container?: HTMLElement }>();

const criteriaStore = useSearchCriteriaStore();
const locationLabels = useLocationLabels();
const breakpoints = useBreakpoints(breakpointsTailwind);

// The floor is also the server-rendered count: the measurement only ever widens it after mount.
// Under `sm` the stops are three-letter codes in a row that never has room for more, so the
// measurement row is not mounted there and the count stays at the floor.
const MIN_VISIBLE_STOPS = 2;
const showsCityNames = breakpoints.greaterOrEqual('sm');
const routeElement = ref<HTMLElement>();
const measureElement = ref<HTMLElement>();
const visibleStops = ref(MIN_VISIBLE_STOPS);

watch(showsCityNames, (shows) => {
  if (!shows) {
    visibleStops.value = MIN_VISIBLE_STOPS;
  }
});
// The siblings' widths change with the committed criteria without resizing the container.
watch(
  () => criteriaStore.committedCriteriaKey,
  () => {
    measure();
  },
  { flush: 'post' },
);
// Observes the refs declared above, so it sits with the watchers rather than with the composable calls.
useResizeObserver([() => properties.container, measureElement], () => {
  measure();
});

// Every stop is laid out once in the invisible row, in the visible row's styles, along with the
// widest possible "… +N"; the visible count is the largest prefix that fits the available room.
const measure = () => {
  const container = properties.container;
  const route = routeElement.value;
  const measures = measureElement.value;
  const stops = [...(measures?.querySelectorAll<HTMLElement>('[data-stop]') ?? [])];
  const badge = measures?.querySelector<HTMLElement>('[data-badge]');
  const lastStop = stops.at(-1);
  // The date, passengers and cabin each render a mobile and a desktop span, one of them `display: none`,
  // so the container's last child has no box below `md`: the siblings end at the last child that has one.
  const lastSiblingRect = [...(container?.children ?? [])]
    .map((sibling) => sibling.getBoundingClientRect())
    .findLast((rect) => rect.width > 0);
  if (!container || !route || !measures || !lastSiblingRect || !badge || !lastStop) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const routeRect = route.getBoundingClientRect();
  const available =
    containerRect.width - (routeRect.left - containerRect.left) - (lastSiblingRect.right - routeRect.right);

  const measuresLeft = measures.getBoundingClientRect().left;
  const rightEdge = (element: HTMLElement) => element.getBoundingClientRect().right - measuresLeft;
  const badgeSpan = rightEdge(badge) - rightEdge(lastStop);
  let count = MIN_VISIBLE_STOPS;
  for (const [index, stop] of stops.entries()) {
    const needed = rightEdge(stop) + (index + 1 < stops.length ? badgeSpan : 0);
    if (needed <= available) {
      count = Math.max(count, index + 1);
    }
  }
  visibleStops.value = count;
};
</script>

<template>
  <span
    ref="routeElement"
    class="relative flex items-center"
  >
    <span
      aria-hidden="true"
      class="flex items-center gap-2 text-xs font-normal text-text-inverse sm:hidden"
    >
      {{ criteriaStore.committedItineraryStops[0]?.code }}
      <ArrowRight
        aria-hidden="true"
        class="h-4 w-4"
      />
      {{ criteriaStore.committedItineraryStops[1]?.code }}
      <template v-if="criteriaStore.committedItineraryStops.length > visibleStops">
        <ArrowRight
          aria-hidden="true"
          class="h-4 w-4"
        />
        <span class="inline-flex items-center gap-1">
          …
          <UiCustomBadge
            variant="translucent"
            radius="m"
            size="s"
          >
            +{{ criteriaStore.committedItineraryStops.length - visibleStops }}
          </UiCustomBadge>
        </span>
      </template>
    </span>
    <span
      aria-hidden="true"
      class="hidden items-center gap-2 text-sm font-normal text-text-inverse sm:inline-flex md:gap-3"
    >
      <template
        v-for="(stop, index) in criteriaStore.committedItineraryStops.slice(0, visibleStops)"
        :key="`${stop.code}-${index}`"
      >
        <ArrowRight
          v-if="index > 0"
          aria-hidden="true"
          class="h-4 w-4"
        />
        {{ locationLabels.city(criteriaStore.committedItineraryCityCodes[index]) }}
      </template>
      <template v-if="criteriaStore.committedItineraryStops.length > visibleStops">
        <ArrowRight
          aria-hidden="true"
          class="h-4 w-4"
        />
        <span class="inline-flex items-center gap-1">
          …
          <UiCustomBadge
            variant="translucent"
            radius="m"
          >
            +{{ criteriaStore.committedItineraryStops.length - visibleStops }}
          </UiCustomBadge>
        </span>
      </template>
    </span>
    <!-- Measurement row, not a spacer: absolute so it takes no room, invisible so it still has a
         layout to read, mounted from `sm` only, where the measurement can change the count. -->
    <span
      v-if="showsCityNames"
      ref="measureElement"
      aria-hidden="true"
      class="invisible absolute left-0 top-0 inline-flex items-center gap-2 whitespace-nowrap text-sm font-normal md:gap-3"
    >
      <span
        v-for="(stop, index) in criteriaStore.committedItineraryStops"
        :key="`${stop.code}-${index}`"
        data-stop
        class="inline-flex items-center gap-2 md:gap-3"
      >
        <ArrowRight
          v-if="index > 0"
          class="h-4 w-4"
        />
        {{ locationLabels.city(criteriaStore.committedItineraryCityCodes[index]) }}
      </span>
      <span
        data-badge
        class="inline-flex items-center gap-2 md:gap-3"
      >
        <ArrowRight class="h-4 w-4" />
        <span class="inline-flex items-center gap-1">
          …
          <UiCustomBadge
            variant="translucent"
            radius="m"
          >
            +{{ criteriaStore.committedItineraryStops.length - MIN_VISIBLE_STOPS }}
          </UiCustomBadge>
        </span>
      </span>
    </span>
  </span>
</template>
